/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { Plus, X } from 'lucide-react'
import { Controller } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '../ui/button'
import { Form } from '../ui/form'
import { FieldWrapper } from '../ui/form/field-wrapper'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'

import {
  CreateQuestionInput,
  createQuestionInputSchema,
  useCreateServiceQuestion,
} from '@/features/service-questions/api/create-question'
import { UpdateServiceQuestionInput, useUpdateServiceQuestion } from '@/features/service-questions/api/edit-question'
import { ServiceQuestion } from '@/features/service-questions/api/get-question'
import { QuestionTypeSelect } from '@/features/service-questions/components/question-type-select'
import { CreateQuestionModal, ModalRenderArgs } from '@/state/modals'

const CreateQuestionModalContent = ({ modal, slots, ctx }: ModalRenderArgs<CreateQuestionModal>) => {
  const { tid, question } = modal
  const isEdit = Boolean(question)
  const { Header, Title, Description, Body, Footer } = slots
  const createQuestionMutation = useCreateServiceQuestion({
    tid,
    mutationConfig: {
      onSuccess: () => {
        toast.success('Question créée')
        ctx.close()
      },
    },
  })

  const updateQuestionMutation = useUpdateServiceQuestion({
    qid: question?.id || '',
    mutationConfig: {
      onSuccess: () => {
        toast.success('Question mise à jour')
        ctx.close()
      },
    },
  })

  const defaultChoices = React.useMemo<string[]>(() => question?.choices?.map((c) => c.choice_text) ?? [], [question])

  return (
    <>
      <Header className="flex items-start justify-between gap-3">
        <div className="space-y-1 text-left">
          <Title className="flex items-center gap-2 text-lg">
            {question ? 'Modifier la question' : 'Créer une question'}
          </Title>
          <Description>
            {question ? 'Modifiez votre question et ses options' : 'Créez une nouvelle question pour vos services'}
          </Description>
        </div>
      </Header>

      <Body>
        <Form
          id="create-question"
          schema={createQuestionInputSchema}
          onSubmit={(values) => {
            const cleanedChoices: string[] = (values.choices || [])
              .map((choice) => choice.trim())
              .filter((choice) => choice.length > 0)

            if (values.type === 'open') {
              cleanedChoices.splice(0, cleanedChoices.length)
            }

            if (isEdit && question?.id) {
              const choiceObjects: ServiceQuestion['choices'] = cleanedChoices.map((choice_text, index) => ({
                choice_text,
                id: question?.choices?.[index]?.id ?? '',
                question_id: question?.id ?? null,
              }))

              const payload: UpdateServiceQuestionInput = {
                text: values.text,
                type: values.type,
                choices: choiceObjects,
              }
              updateQuestionMutation.mutate({ qid: question.id, values: payload })
              return
            }

            createQuestionMutation.mutate({ tid, values: { ...values, choices: cleanedChoices } })
          }}
          options={{
            defaultValues: {
              type: question?.type ?? 'open',
              text: question?.text ?? '',
              choices: defaultChoices,
            } as CreateQuestionInput,
          }}
        >
          {({ register, formState, control, watch }) => {
            const type = watch('type')

            return (
              <>
                <div className="space-y-2">
                  <FieldWrapper label="Question *" error={formState.errors['text']}>
                    <Textarea placeholder="Entrez votre question..." {...register('text')} rows={3} />
                  </FieldWrapper>
                </div>

                <div className="space-y-2">
                  <FieldWrapper label="Type de questions *" error={formState.errors['type']}>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => <QuestionTypeSelect value={field.value} onValueChange={field.onChange} />}
                    />
                  </FieldWrapper>
                  <p className="text-xs text-muted-foreground">
                    {type === 'open' && 'Réponse libre de type texte'}
                    {type === 'single_choice' && 'Une seule option peut être sélectionnée'}
                    {type === 'multiple_choice' && 'Plusieurs options peuvent être sélectionnées'}
                  </p>
                </div>

                {type !== 'open' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Options de réponse</Label>
                      <Controller
                        name="choices"
                        control={control}
                        render={({ field }) => (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => field.onChange([...(field.value || []), ''])}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Ajouter
                          </Button>
                        )}
                      />
                    </div>

                    <div className="space-y-2">
                      <Controller
                        name="choices"
                        control={control}
                        render={({ field }) => {
                          const options = field.value || []

                          const updateOption = (index: number, value: string) => {
                            const next = [...options]
                            next[index] = value
                            field.onChange(next)
                          }

                          const removeOption = (index: number) => {
                            const next = options.filter((_, i) => i !== index)
                            field.onChange(next)
                          }

                          return (
                            <>
                              {options.map((option: string, index: number) => (
                                <div key={index} className="flex gap-2">
                                  <Input
                                    placeholder={`Option ${index + 1}`}
                                    value={option}
                                    onChange={(e) => updateOption(index, e.target.value)}
                                    required
                                  />
                                  <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(index)}>
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Supprimer</span>
                                  </Button>
                                </div>
                              ))}
                              {options.length === 0 && (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                  Aucune option ajoutée. Cliquez sur &quot;Ajouter&quot; pour créer des options.
                                </p>
                              )}
                            </>
                          )
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )
          }}
        </Form>
      </Body>

      <Footer className="flex flex-row justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={() => ctx.close()}>
          Annuler
        </Button>
        <Button
          type="submit"
          form="create-question"
          isLoading={isEdit ? updateQuestionMutation.isPending : createQuestionMutation.isPending}
        >
          {question ? 'Enregistrer' : 'Créer'}
        </Button>
      </Footer>
    </>
  )
}

export function render(args: ModalRenderArgs<CreateQuestionModal>) {
  return <CreateQuestionModalContent {...args} />
}
